//REACT
import { useState, useEffect, useCallback, useMemo } from 'react';

//MUI
import { MenuItem } from '@mui/material';

//STYLES
import {
    InlineChatContainer,
    ChatHeader,
    HeaderInfo,
    CustomSelect,
    LoadingContainer,
    EmptyChatMessage,
} from './styles/CommentModalStyles';

//COMPONENTS
import Text from '../../common/Text';
import CommentsChat from './CommentsChat';
import SimpleLoading from '../../common/SimpleLoading';

//UTILS
import {
    formatCommentDate,
    formatCommentDateTime,
    getCommentSortMs,
} from '../../../utils/dates/dates.utils';
import {
    buildLegacyCommentsFromSubmission,
    mergeLegacyAndConversationComments,
    isAdiProfessorEmail,
} from './legacyComments.utils';

//REDUX
import { useSelector } from 'react-redux';
import {
    getStudentConversationsResource,
    postCommentResource,
    postCommentAndCreateConversationResource,
} from '../../../redux/api/comments';

const pickAttachmentUrl = (v) =>
    v && typeof v === 'object' && v.url ? v.url : null;

const StudentCommentsModal = (props) => {
    const {
        resourceId,
        subjectId,
        learningObject,
        teachers = [],
        legacySubmission = null,
        legacyStudent = null,
        legacyProfessor = null,
    } = props;

    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);
    const [resourceName, setResourceName] = useState('');
    const [conversationId, setConversationId] = useState(null);

    const user = useSelector((state) => state?.auth?.user);
    const studentId = user?.repo_id;
    const legacyComments = useMemo(
        () =>
            buildLegacyCommentsFromSubmission({
                submission: legacySubmission,
                student: legacyStudent,
                professor: legacyProfessor,
            }),
        [legacyProfessor, legacyStudent, legacySubmission]
    );

    const legacyMergeOptions = useMemo(
        () => ({
            submissionId:
                legacySubmission?.id ?? legacySubmission?.submission_id ?? null,
            isAdiProfessor: isAdiProfessorEmail(
                legacyProfessor?.email ?? legacySubmission?.professor?.email
            ),
        }),
        [legacyProfessor, legacySubmission]
    );

    const loadConversation = useCallback(async () => {
        if (!resourceId) return;

        try {
            setIsLoading(true);
            const response = await getStudentConversationsResource(
                resourceId,
                subjectId
            );

            if (response && !response.error) {
                setResourceName(response.resource_name || '');

                if (
                    response.conversations &&
                    Array.isArray(response.conversations) &&
                    response.conversations.length > 0
                ) {
                    const firstConversation = response.conversations[0];
                    setConversationId(firstConversation.id);

                    if (
                        firstConversation.teacher_ids &&
                        Array.isArray(firstConversation.teacher_ids) &&
                        firstConversation.teacher_ids.length > 0
                    ) {
                        setSelectedTeacherId(firstConversation.teacher_ids[0]);
                    }

                    if (
                        firstConversation.messages &&
                        Array.isArray(firstConversation.messages) &&
                        firstConversation.messages.length > 0
                    ) {
                        const transformedComments =
                            firstConversation.messages.map((messageItem) => {
                                const att = messageItem.attachments || {};
                                const fileUrl =
                                    pickAttachmentUrl(att.file) ||
                                    (messageItem.attachment &&
                                    messageItem.attachment.url
                                        ? messageItem.attachment.url
                                        : null);
                                const submissionIdNum = Number(
                                    messageItem.submission_id
                                );
                                return {
                                    id: messageItem.id,
                                    name: messageItem.author_name,
                                    comment: messageItem.message,
                                    date:
                                        formatCommentDate(
                                            messageItem.create_date
                                        ) +
                                        ' ' +
                                        formatCommentDateTime(
                                            messageItem.create_date
                                        ),
                                    photo: messageItem.author_picture || null,
                                    sender: messageItem.author_is_teacher
                                        ? 'user'
                                        : 'other',
                                    author_is_teacher:
                                        messageItem.author_is_teacher,
                                    comment_type:
                                        messageItem.comment_type || 'normal',
                                    submission_id: Number.isNaN(submissionIdNum)
                                        ? null
                                        : submissionIdNum,
                                    sort_ms: getCommentSortMs(
                                        messageItem.create_date
                                    ),
                                    image: fileUrl,
                                    attachments: {
                                        fileUrl,
                                        audioUrl: pickAttachmentUrl(att.audio),
                                        videoUrl: pickAttachmentUrl(att.video),
                                    },
                                };
                            });

                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                transformedComments,
                                legacyMergeOptions
                            )
                        );
                    } else {
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                [],
                                legacyMergeOptions
                            )
                        );
                    }
                } else {
                    setConversationId(null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            [],
                            legacyMergeOptions
                        )
                    );

                    if (
                        teachers &&
                        Array.isArray(teachers) &&
                        teachers.length > 0
                    ) {
                        setSelectedTeacherId(teachers[0].id);
                    }
                }
            } else if (response && response.error) {
                console.error(
                    'Error al obtener las conversaciones:',
                    response.error
                );
                setConversationId(null);
                setComments(
                    mergeLegacyAndConversationComments(
                        legacyComments,
                        [],
                        legacyMergeOptions
                    )
                );

                if (
                    teachers &&
                    Array.isArray(teachers) &&
                    teachers.length > 0
                ) {
                    setSelectedTeacherId(teachers[0].id);
                }
            } else {
                setConversationId(null);
                setComments(
                    mergeLegacyAndConversationComments(
                        legacyComments,
                        [],
                        legacyMergeOptions
                    )
                );

                if (
                    teachers &&
                    Array.isArray(teachers) &&
                    teachers.length > 0
                ) {
                    setSelectedTeacherId(teachers[0].id);
                }
            }
        } catch (error) {
            console.error('Error al obtener las conversaciones:', error);
            setConversationId(null);
            setComments(
                mergeLegacyAndConversationComments(
                    legacyComments,
                    [],
                    legacyMergeOptions
                )
            );

            if (teachers && Array.isArray(teachers) && teachers.length > 0) {
                setSelectedTeacherId(teachers[0].id);
            }
        } finally {
            setIsLoading(false);
        }
    }, [legacyComments, legacyMergeOptions, resourceId, teachers]);

    useEffect(() => {
        if (resourceId) {
            loadConversation();
        }
    }, [resourceId, studentId, loadConversation]);

    useEffect(() => {
        if (
            !conversationId &&
            teachers &&
            Array.isArray(teachers) &&
            teachers.length > 0 &&
            !selectedTeacherId
        ) {
            setSelectedTeacherId(teachers[0].id);
        }
    }, [conversationId, teachers, selectedTeacherId]);

    const handleSelectTeacher = (teacherId) => {
        setSelectedTeacherId(teacherId);
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                if (file.size >= 5242880) {
                    alert('El tamaño máximo permitido es 5MB');
                    e.target.value = '';
                    return;
                }
                setSelectedImage(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const buildNewCommentFromResponse = (response, fallbackMessage) => {
        const resAtt = response.attachments || {};
        const fileUrl =
            pickAttachmentUrl(resAtt.file) ||
            (response.attachment &&
            response.attachment !== false &&
            response.attachment.url
                ? response.attachment.url
                : null);
        return {
            id: response.message_id || response.id,
            name: response.author_name || '',
            comment: response.message || fallbackMessage,
            date: response.create_date
                ? formatCommentDate(response.create_date) +
                  ' ' +
                  formatCommentDateTime(response.create_date)
                : new Date().toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                  }) +
                  ' ' +
                  new Date().toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit',
                  }) +
                  ' hs',
            photo: response.author_picture || null,
            sender: response.author_is_teacher ? 'user' : 'other',
            author_is_teacher: response.author_is_teacher,
            comment_type: response.comment_type || 'normal',
            image: fileUrl,
            attachments: {
                fileUrl,
                audioUrl: pickAttachmentUrl(resAtt.audio),
                videoUrl: pickAttachmentUrl(resAtt.video),
            },
            sort_ms: response.create_date
                ? getCommentSortMs(response.create_date)
                : Date.now(),
        };
    };

    const handleSendMessage = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!comment.trim()) return;
        if (!selectedTeacherId) {
            console.error('No hay profesor seleccionado');
            return;
        }

        setIsSendingMessage(true);

        try {
            let response;

            if (!conversationId) {
                if (!resourceId || !studentId || !selectedTeacherId) {
                    console.error('Faltan datos necesarios:', {
                        resourceId,
                        studentId,
                        selectedTeacherId,
                    });
                    setIsSendingMessage(false);
                    return;
                }

                const formData = new FormData();
                formData.append('message', comment);
                formData.append('teacher_id', String(selectedTeacherId));
                formData.append('student_id', String(studentId));
                formData.append('resource_id', String(resourceId));
                formData.append('subject_id', String(subjectId ?? ''));
                formData.append(
                    'learning_object_id',
                    String(learningObject ?? '')
                );
                if (selectedImage instanceof File) {
                    formData.append('attachment', selectedImage);
                }

                response = await postCommentAndCreateConversationResource(
                    resourceId,
                    formData
                );

                if (response && !response.error) {
                    const conversationIdFromResponse =
                        response.conversation_id || response.id;

                    if (conversationIdFromResponse) {
                        setConversationId(conversationIdFromResponse);
                    }

                    const messageId = response.message_id || response.id;
                    if (messageId || response.author_name) {
                        const newComment = buildNewCommentFromResponse(
                            response,
                            comment
                        );

                        setComments((prevComments) => [
                            ...prevComments,
                            newComment,
                        ]);
                        setComment('');
                        setSelectedImage(null);
                        setImagePreview(null);
                    }
                } else {
                    console.error(
                        'Error al crear conversación:',
                        response?.error || 'Respuesta inválida'
                    );
                }
            } else {
                const formData = new FormData();
                formData.append('message', comment);
                formData.append('teacher_id', String(selectedTeacherId));
                formData.append('student_id', String(studentId));
                formData.append('resource_id', String(resourceId));
                if (selectedImage instanceof File) {
                    formData.append('attachment', selectedImage);
                }

                response = await postCommentResource(conversationId, formData);

                if (response && !response.error && response.id) {
                    const newComment = buildNewCommentFromResponse(
                        response,
                        comment
                    );

                    setComments((prevComments) => [
                        ...prevComments,
                        newComment,
                    ]);
                    setComment('');
                    setSelectedImage(null);
                    setImagePreview(null);
                } else {
                    console.error(
                        'Error al enviar el comentario:',
                        response?.error || 'Respuesta inválida'
                    );
                }
            }
        } catch (error) {
            console.error('Error al enviar el comentario:', error);
        } finally {
            setIsSendingMessage(false);
        }
    };

    return (
        <InlineChatContainer>
            {isLoading ? (
                <LoadingContainer>
                    <SimpleLoading />
                </LoadingContainer>
            ) : (
                <>
                    {comments.length === 0 && (
                        <EmptyChatMessage>
                            <Text fontSize="16px" color="#616161">
                                No hay comentarios. Sé el primero en escribir.
                            </Text>
                        </EmptyChatMessage>
                    )}
                    <CommentsChat
                        comments={comments}
                        imagePreview={imagePreview}
                        handleRemoveImage={handleRemoveImage}
                        handleImageChange={handleImageChange}
                        handleSendMessage={handleSendMessage}
                        comment={comment}
                        setComment={setComment}
                        isLoading={isSendingMessage}
                        selectedImage={selectedImage}
                    />
                </>
            )}
        </InlineChatContainer>
    );
};

export default StudentCommentsModal;
